import type { Interpolation, Theme } from '@emotion/react';
import { Fragment, memo, type ReactNode, type RefObject, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { ImpulseSpinner } from '../../general/loading/impulse-spinner/ImpulseSpinner';

export type InfiniteScrollProps = {
  children: ReactNode;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  loadingSpinnerCss?: Interpolation<Theme>;
  root?: RefObject<HTMLElement>;
  threshold?: number;
  rootMargin?: string;
};

/**
 * InfiniteScroll is a component that allows to load more data when the user scrolls to the bottom of the page.
 * It uses the `react-intersection-observer` library to detect when the user scrolls to the bottom of the page.
 * It also uses the `useInView` hook to detect when the user scrolls to the bottom of the page.
 * It also uses the `useEffect` hook to load more data when the user scrolls to the bottom of the page.
 */
const InfiniteScrollContainer = ({
  children,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  loadingSpinnerCss,
  root,
  threshold = 0.2,
  rootMargin = '80px 0px',
}: InfiniteScrollProps) => {
  const { ref: sentinelRef, inView } = useInView({
    root: root?.current ?? undefined,
    threshold,
    rootMargin,
  });

  // useEffect hook to load more data when the user scrolls to the bottom of the page.
  useEffect(() => {
    if (inView && hasNextPage) {
      onLoadMore();
    }
  }, [inView, hasNextPage, onLoadMore]);

  return (
    <Fragment>
      {children}

      {hasNextPage && <div ref={sentinelRef} style={{ height: 32 }} />}

      {isFetchingNextPage && <ImpulseSpinner css={loadingSpinnerCss} />}
    </Fragment>
  );
};

export const InfiniteScroll = memo(InfiniteScrollContainer);
